<template>
  <div>
    <b-overlay
      :show="macrosesResponses('getMacroses') && macrosesEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Macros Variables
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Macros Variables</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="macrosesResponses('addMacros') || macrosesResponses('updateMacros')"
            @click="[
              addNewMacrosField(),
              scrollToBottom(),
              focusInput('null-name')
            ]">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Macros</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border"
        style="min-height: 300px;">
        
        <b-card-body>
          <b-table
            :items="macrosesList"
            :fields="fields"
            :class="{ 'table-hover' : !macrosesEmpty }"
            stacked="md"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(name)="data">
              <b-form-input
                v-if="data.item.editable"
                :id="`input-${data.item._id}-name`"
                size="sm"
                placeholder="Name"
                autocomplete="off"
                :disabled="!isNewMacrosField(data.item._id)"
                :state="macrosFieldInvalidState(data.item._id, 'name')"
                v-model="data.item.name" />
              <span
                v-else
                class="d-inline-block text-truncate line-height-xl">{{ data.item.name }}</span>
            </template>
            <template #cell(value)="data">
              <b-form-input
                v-if="data.item.editable"
                :id="`input-${data.item._id}-value`"
                size="sm"
                placeholder="Value"
                :state="macrosFieldInvalidState(data.item._id, 'value')"
                v-model="data.item.value" />
              <span
                v-else
                class="d-inline-block text-truncate  line-height-xl">{{ data.item.value }}</span>
            </template>
            <template #cell(edit)="data">
              <template v-if="data.item.editable">
                <b-button
                  v-if="data.item._id"
                  size="sm"
                  variant="outline-primary"
                  class="mr-3 align-middle btn-icon"
                  @click="[
                    clearErrors(data.item._id),
                    switchEditableMode({
                      macros: data.item,
                      status: false
                    }),
                  ]">
                  <i class="fa fa-times fa-sm"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-4 align-middle"
                  :disabled="macrosesResponses('addMacros') || macrosesResponses('updateMacros')"
                  @click="[
                    clearErrors(data.item._id),
                    handleMacros(data.item)
                  ]">
                  <i class="fa fa-save fa-sm"></i>
                  <span>Save</span>
                </b-button>
              </template>
              <template v-else>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-4 align-middle"
                  @click="[
                    selectMacros(data.item),
                    switchEditableMode({
                      macros: data.item,
                      status: true
                    }),
                    focusInput(`${data.item._id}-value`)
                  ]">
                  <i class="fa fa-pen fa-sm"></i>
                  <span>Edit</span>
                </b-button>
              </template>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="macrosesResponses('deleteMacros')"
                @click="[
                  selectMacros(data.item),
                  handleDelete(data.item._id),
                ]">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="macros in macrosesList"
      :key="`popover-${macros._id}`"
      :target="`deleteButton-${macros._id}`"
      :ref="`popover-${macros._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete macros variable <span class="font-weight-bold">{{ selectedMacros.name }}</span>?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(macros._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(macros._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>
  </div>
</template>

<script>
import {
  // actions
  GET_MACROSES,
  ADD_MACROS,
  UPDATE_MACROS,
  DELETE_MACROS,

  // MUTATIONS
  CLEAR_MACROSES,
  SELECT_MACROS,
  UNSELECT_MACROS,
  SWITCH_MACROS_EDITABLE_STATE,
  REMOVE_MACROS,
  ADD_NEW_MACROS_FIELD,
  CLEAR_MANAGE_MACROS_ERRORS,
} from '@/core/services/store/macroses.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  focusInput,
  scrollToBottom,
  showDeletePopover,
  hideDeletePopover,
} from '@/core/utils'

export default {
  name: 'SettingsMacroses',
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold', 'text-nowrap', 'pl-5'],
          thStyle: 'width: 30%;'
        },
        {
          label: 'Value',
          key: 'value',
          class: ['align-middle', 'text-nowrap'],
          thStyle: 'width: 30%;'
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5'],
        }
      ],

      showToast,
      focusInput,
      scrollToBottom,
      showDeletePopover,
      hideDeletePopover,
    }
  },
  computed: {
    ...mapGetters([
      'macrosesList',
      'totalMacroses',
      'macrosesEmpty',
      'selectedMacros',
      'selectedMacrosId',
      'noEmptyMacrosFields',
      'isNewMacrosField',
      'macrosFieldInvalidState',
      'macrosesResponses',
    ]),
  },
  created() {
    this.clearMacroses()
    this.getMacroses()
  },
  mounted() {
    eventBus.$on('showToast/SettingsMacroses', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    eventBus.$off('showToast/SettingsMacroses')
  },
  methods: {
    getMacroses() {
      this.$store.dispatch(GET_MACROSES, {})
    },

    clearMacroses() {
      this.$store.commit(CLEAR_MACROSES)
    },

    selectMacros(macros) {
      this.$store.commit(SELECT_MACROS, macros)
    },

    unselectMacros() {
      this.$store.commit(UNSELECT_MACROS)
    },

    handleMacros(macros) {
      this.$store.dispatch(
        this.isNewMacrosField(macros._id)
          ? ADD_MACROS
          : UPDATE_MACROS,
        macros
      )
    },

    handleDelete(id) {
      if (id) {
        this.showDeletePopover(id)
      } else {
        this.clearErrors(null)
        this.removeMacros(null)
      }
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_MACROS)
    },

    switchEditableMode({ macros, status }) {
      this.$store.commit(SWITCH_MACROS_EDITABLE_STATE, { macros, status })
    },

    addNewMacrosField() {
      this.$store.commit(ADD_NEW_MACROS_FIELD, {})
    },

    removeMacros(id) {
      this.$store.commit(REMOVE_MACROS, id)
    },

    clearErrors(id) {
      this.$store.commit(CLEAR_MANAGE_MACROS_ERRORS, id)
    },
  }
}
</script>
